import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import BriefListingCard from 'components/BriefListingCard/BriefListingCard';
import JobListing from 'components/JobListing/JobListing';
import noActivity from './no-activity.png';
import JobsBoardBanner from '../../containers/Ads/JobsBoardBanner/JobsBoardBanner';
import SignUp from 'components/SignUp/SignUp';
import useAuth from 'hooks/useAuth';
import ApplicationWriterUpsell from 'components/ApplicationWriterUpsell/ApplicationWriterUpsell';
import BrowseMoreJobs from 'components/BrowseMoreJobs/BrowseMoreJobs';
import JobsBestMatchCard from 'components/JobsBestMatchCard/JobsBestMatchCard';
import PostJobUpsell from 'components/PostJobUpsell/PostJobUpsell';
import { fromSeoUrl } from 'helpers/HelperFunctions';
import styles from './BriefListings.module.scss';

/** Partner ads after the job at index 1, then every 15th job (14, 29, 44…). */
export const shouldInjectJobsBoardAd = (jobIndex) =>
  jobIndex === 1 || (jobIndex + 1) % 15 === 0;

/** Best match card sits after the job at this index (inside the first batch). */
export const JOBS_BEST_MATCH_CARD_INDEX = 4;

/** Guest browse-more box after the last job of the first API batch (limit 10). */
export const JOBS_BROWSE_MORE_INDEX = 9;

const BriefListings = ({
  briefs,
  loaded,
  loading: _loading,
  selectedRole,
  paid,
  onClickRole,
  onClickLocation,
  country,
  city,
  partners,
  loadedPartners,
  loadingPartners,
  pageType,
  auth,
  filterCount,
  onBestMatchNavigate,
  showBestMatchCard,
  ...rest
}) => {
  const { user } = useAuth();

  const role =
    selectedRole && selectedRole.name ? fromSeoUrl(selectedRole.name) : '';
  let jobsBoardBannerCount = 0;
  const maxBannerCount = 5;

  return (
    <div {...rest}>
      {loaded ? (
        <div>
          {briefs.length > 0 ? (
            <div>
              {briefs.map((brief, idx) => {
                const injectAd =
                  shouldInjectJobsBoardAd(idx) &&
                  jobsBoardBannerCount < maxBannerCount;

                if (injectAd) {
                  jobsBoardBannerCount++;
                }

                const isLastBrief = idx === briefs.length - 1;
                const showBrowseMore =
                  auth === 'guest' &&
                  (idx === JOBS_BROWSE_MORE_INDEX ||
                    (briefs.length <= JOBS_BROWSE_MORE_INDEX && isLastBrief));

                const briefListing = (
                  <JobListing
                    brief={brief}
                    key={brief.id}
                    onClickRole={onClickRole}
                    onClickLocation={onClickLocation}
                  />
                );

                return (
                  <Fragment key={`briefListing-${brief.id}`}>
                    {briefListing}
                    {injectAd && (
                      <JobsBoardBanner
                        partners={partners}
                        loadedPartners={loadedPartners}
                        loadingPartners={loadingPartners}
                      />
                    )}
                    {showBestMatchCard &&
                      idx === JOBS_BEST_MATCH_CARD_INDEX && (
                        <JobsBestMatchCard
                          pageType={pageType}
                          auth={auth}
                          filterCount={filterCount}
                          onBestMatchNavigate={onBestMatchNavigate}
                        />
                      )}
                    {showBrowseMore && (
                      <>
                        {idx === JOBS_BROWSE_MORE_INDEX && (
                          <div
                            className={styles.batchDivider}
                            data-testid="jobs-batch-divider"
                          >
                            <hr className={styles.batchDividerRule} />
                            <span className={styles.batchDividerLabel}>
                              End of the first batch of jobs
                            </span>
                            <hr className={styles.batchDividerRule} />
                          </div>
                        )}
                        <BrowseMoreJobs placement="mobile" country={country} />
                      </>
                    )}
                    {idx === 9 && (
                      <ApplicationWriterUpsell user={user} mobile={true} />
                    )}
                    {idx === 19 && (
                      <PostJobUpsell
                        mobile={true}
                        role={role}
                        country={country}
                        city={city}
                      />
                    )}
                  </Fragment>
                );
              })}
            </div>
          ) : (
            <div className="mt++ text-center">
              <img src={noActivity} alt="Twine" />
              <p className="mt++">
                No
                {selectedRole !== null
                  ? ` ${selectedRole.name.toLowerCase()}`
                  : ''}{' '}
                {paid ? 'jobs' : 'collaborations'} found.
              </p>
              {!user ? (
                <>
                  <p>
                    Sign up to Twine below for free and get notified instantly
                    when jobs are available.
                  </p>
                  <SignUp
                    redirect={'onboarding'}
                    redirectFromGoogle={'signup'}
                    defaultAccountType={'seller'}
                  />
                  <BrowseMoreJobs placement="mobile" country={country} />
                </>
              ) : (
                <>
                  <p>
                    Turn on <a href="/settings">job alert notifications</a> to
                    be notified when the next job is posted.
                  </p>
                  <p>
                    <a href="/pricing/creatives">Upgrade to Twine Pro</a> to
                    increase your chances of winning work.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        [...Array(3)].map((_, idx) => (
          <BriefListingCard brief={null} key={idx} />
        ))
      )}
    </div>
  );
};

BriefListings.propTypes = {
  briefs: PropTypes.array,
  loaded: PropTypes.bool,
  loading: PropTypes.bool, // retained for callers; load-more UI lives in ProjectsBase
  selectedRole: PropTypes.object,
  paid: PropTypes.bool,
  onClickRole: PropTypes.func,
  onClickLocation: PropTypes.func,
  country: PropTypes.string,
  city: PropTypes.string,
  partners: PropTypes.array,
  loadedPartners: PropTypes.bool,
  loadingPartners: PropTypes.bool,
  pageType: PropTypes.oneOf(['jobs', 'role', 'country']),
  auth: PropTypes.oneOf(['guest', 'logged_in']),
  filterCount: PropTypes.number,
  onBestMatchNavigate: PropTypes.func,
  showBestMatchCard: PropTypes.bool,
};

BriefListings.defaultProps = {
  pageType: 'jobs',
  auth: 'guest',
  filterCount: 0,
  onBestMatchNavigate: undefined,
  showBestMatchCard: true,
};

export default BriefListings;
